export interface IndexedDbKeyValueStore {
  get(key: string): Promise<unknown>;

  put(value: unknown, key: string): Promise<void>;

  remove(key: string): Promise<void>;
}

/**
 * A tiny promise-wrapped IndexedDB key→value store backed by a single object store, so
 * callers don't repeat the open/upgrade/transaction/close boilerplate. Each operation
 * opens and closes its own connection — fine for the low-frequency stash/read/clear flows
 * that use it. Callers should handle rejections (quota, private browsing) themselves.
 */
export const createIndexedDbKeyValueStore = (dbName: string, storeName: string, version = 1): IndexedDbKeyValueStore => {
  const openDb = async (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbName, version);
      request.onupgradeneeded = (): void => {
        request.result.createObjectStore(storeName);
      };
      request.onsuccess = (): void => {
        resolve(request.result);
      };
      request.onerror = (): void => {
        reject(request.error ?? new Error(`Failed to open IndexedDB: ${dbName}`));
      };
    });
  };

  const run = async (mode: IDBTransactionMode, runRequest: (store: IDBObjectStore) => IDBRequest): Promise<unknown> => {
    const db = await openDb();
    try {
      return await new Promise<unknown>((resolve, reject) => {
        const request = runRequest(db.transaction(storeName, mode).objectStore(storeName));
        request.onsuccess = (): void => {
          resolve(request.result);
        };
        request.onerror = (): void => {
          reject(request.error ?? new Error(`IndexedDB request failed: ${dbName}`));
        };
      });
    } finally {
      db.close();
    }
  };

  return {
    get: (key: string): Promise<unknown> => run('readonly', (store) => store.get(key)),
    put: async (value: unknown, key: string): Promise<void> => {
      await run('readwrite', (store) => store.put(value, key));
    },
    remove: async (key: string): Promise<void> => {
      await run('readwrite', (store) => store.delete(key));
    },
  };
};
